import { useCallback, useState } from "react";
import { Modal } from "antd";
import type {
  MotionStructuralConnectorAction,
  MotionStructuralImpactReport,
} from "@blings/blings-player/editor-tools";
import { useMst } from "../../stores/main";
import type { LayerInspectorSelection } from "../../helpers/layerInspectorTools";

const MAX_WARNING_ITEMS = 5;

type LayerStructuralActionArgs = {
  selections?: LayerInspectorSelection[];
  primarySelection?: LayerInspectorSelection;
};
type LayerStructuralActionHandler = (
  args?: LayerStructuralActionArgs
) => Promise<void>;
type LayerCopyActionHandler = (
  args?: LayerStructuralActionArgs
) => Promise<string | undefined>;
type LayerPasteActionHandler = (clipboardText?: string) => Promise<void>;
export type LayerSortAction =
  | "bringToFront"
  | "bringForward"
  | "sendBackward"
  | "sendToBack";
type LayerSortActionHandler = (
  action: LayerSortAction,
  args?: LayerStructuralActionArgs
) => Promise<void>;
type UseLayerStructuralActionsResult = {
  copyLayers: LayerCopyActionHandler;
  deleteLayers: LayerStructuralActionHandler;
  duplicateLayers: LayerStructuralActionHandler;
  pasteLayers: LayerPasteActionHandler;
  sortLayers: LayerSortActionHandler;
  isApplyingLayerAction: boolean;
};

const CONNECTOR_LABELS: Record<string, string> = {
  asset: "Media",
  custom: "Custom",
  hideLayer: "Hide",
  interactiveConcat: "Branch timeline",
  interactiveForm: "Submit form",
  interactiveInput: "Input",
  interactiveOpen: "Open link",
  text: "Text",
  themeColor: "Color",
  transform: "Transform",
};

const PROPERTY_LABELS: Record<string, string> = {
  className: "CSS class",
  fillColor: "fill color",
  hidden: "visibility",
  imageName: "media",
  opacity: "opacity",
  position: "position",
  positionX: "X position",
  positionY: "Y position",
  rotation: "rotation",
  scale: "scale",
  scaleX: "X scale",
  scaleY: "Y scale",
  shapePath: "shape path",
  solidColor: "solid color",
  text: "text content",
  textFillColor: "text fill color",
  textStrokeColor: "text stroke color",
};

function humanizeIdentifier(value: string): string {
  return value
    .replace(/\./g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .trim()
    .toLowerCase();
}

function getConnectorTypeLabel(modType: string): string {
  return CONNECTOR_LABELS[modType] ?? humanizeIdentifier(modType);
}

function getConnectorArticle(label: string): string {
  return /^[aeiou]/i.test(label) ? "an" : "a";
}

function getTargetPropertyLabel(property?: string): string {
  if (!property) return "layer target";
  return PROPERTY_LABELS[property] ?? humanizeIdentifier(property);
}

function isDisabledConnection(
  action: MotionStructuralConnectorAction
): boolean {
  return (
    Boolean(action.connection.disabled) ||
    Boolean((action.connection.mod as { isDisabled?: boolean })?.isDisabled)
  );
}

function getConnectorActionLabel(
  action: MotionStructuralConnectorAction
): string {
  const layerLabel = action.sourceTarget.layerName || "Selected layer";
  const propertyLabel = getTargetPropertyLabel(
    action.connection.target.property
  );
  const connectorLabel = getConnectorTypeLabel(action.connection.modType);
  const connectorArticle = isDisabledConnection(action)
    ? "a disabled"
    : getConnectorArticle(connectorLabel);
  const targetLabel =
    propertyLabel === "layer target"
      ? layerLabel
      : `${propertyLabel} on ${layerLabel}`;

  return `${targetLabel} is used by ${connectorArticle} ${connectorLabel} connector.`;
}

function getAdvancedImpactCount(
  impactReport: MotionStructuralImpactReport
): number {
  return new Set([
    ...impactReport.expressionConnectionIds,
    ...impactReport.functionConnectionIds,
    ...impactReport.runtimeOnlyConnectionIds,
  ]).size;
}

export function useLayerStructuralActions(): UseLayerStructuralActionsResult {
  const { playerStore } = useMst();
  const [isApplyingLayerAction, setIsApplyingLayerAction] = useState(false);

  const showBlockedWarning = useCallback((message?: string) => {
    Modal.warning({
      width: 520,
      autoFocusButton: "cancel",
      className:
        "unsaved-changes-modal blings-confirm-modal layer-structural-confirm-modal",
      icon: null,
      title: "Layer action could not be applied",
      content: (
        <div className="popup-text">
          {message ?? "The selected layer could not be changed."}
        </div>
      ),
      okText: "Close",
      okType: "text",
      okButtonProps: {
        id: "modal-ok",
        className: "layer-structural-confirm-modal__text-button",
      },
    });
  }, []);

  const duplicateLayers = useCallback(
    async ({
      selections,
      primarySelection,
    }: LayerStructuralActionArgs = {}) => {
      if (isApplyingLayerAction) return;

      setIsApplyingLayerAction(true);
      try {
        const result = await playerStore.duplicateLayerSelections(
          selections,
          primarySelection
        );
        if (result?.blockedReason) {
          showBlockedWarning(result.blockedReason);
        }
      } finally {
        setIsApplyingLayerAction(false);
      }
    },
    [isApplyingLayerAction, playerStore, showBlockedWarning]
  );

  const copyLayers = useCallback(
    async ({ selections }: LayerStructuralActionArgs = {}) => {
      if (isApplyingLayerAction) return undefined;

      setIsApplyingLayerAction(true);
      try {
        const result = await playerStore.copyLayerSelections(selections);
        if (result?.blockedReason) {
          showBlockedWarning(result.blockedReason);
          return undefined;
        }

        return result?.clipboardText;
      } finally {
        setIsApplyingLayerAction(false);
      }
    },
    [isApplyingLayerAction, playerStore, showBlockedWarning]
  );

  const pasteLayers = useCallback(
    async (clipboardText?: string) => {
      if (isApplyingLayerAction) return;

      setIsApplyingLayerAction(true);
      try {
        const result = await playerStore.pasteLayerClipboardText(clipboardText);
        if (result?.blockedReason) {
          showBlockedWarning(result.blockedReason);
        }
      } finally {
        setIsApplyingLayerAction(false);
      }
    },
    [isApplyingLayerAction, playerStore, showBlockedWarning]
  );

  const deleteLayers = useCallback(
    async ({ selections }: LayerStructuralActionArgs = {}) => {
      if (isApplyingLayerAction) return;

      setIsApplyingLayerAction(true);
      try {
        const preview = await playerStore.previewDeleteLayerSelections(
          selections
        );
        if (!preview) return;

        if (preview.blockedReason) {
          showBlockedWarning(preview.blockedReason);
          return;
        }

        const breakingActions = preview.impactReport.connectorActions.filter(
          (action) => action.kind === "willBreak"
        );
        if (!breakingActions.length) {
          await playerStore.deleteLayerSelections(selections);
          return;
        }

        const visibleActions = breakingActions.slice(0, MAX_WARNING_ITEMS);
        const hiddenCount = breakingActions.length - visibleActions.length;
        const disabledCount = breakingActions.filter(isDisabledConnection)
          .length;
        const advancedImpactCount = getAdvancedImpactCount(
          preview.impactReport
        );
        const targetCount = new Set(
          breakingActions.map((action) => action.sourceTarget.path)
        ).size;
        const targetCopy =
          targetCount > 1 ? "These layers are" : "This layer is";
        const connectorCopy =
          breakingActions.length === 1
            ? "1 connector"
            : `${breakingActions.length} connectors`;

        Modal.confirm({
          width: 520,
          autoFocusButton: "cancel",
          className:
            "unsaved-changes-modal blings-confirm-modal layer-structural-confirm-modal",
          icon: null,
          title:
            targetCount > 1
              ? "Delete layers with connector links?"
              : "Delete layer with connector links?",
          content: (
            <div className="popup-text layer-structural-confirm">
              <p>
                {targetCopy} used by {connectorCopy}. Deleting will leave these
                connector controls without this layer target:
              </p>
              <ul>
                {visibleActions.map((action, index) => (
                  <li key={`${action.connection.id}-${index}`}>
                    {getConnectorActionLabel(action)}
                  </li>
                ))}
              </ul>
              {hiddenCount > 0 && (
                <p>
                  {hiddenCount === 1
                    ? "1 more connector will also lose its target."
                    : `${hiddenCount} more connectors will also lose their targets.`}
                </p>
              )}
              {disabledCount > 0 && (
                <p className="layer-structural-confirm__note">
                  Disabled connectors are included because their saved layer
                  links will still be removed.
                </p>
              )}
              {advancedImpactCount > 0 && (
                <p className="layer-structural-confirm__note">
                  {advancedImpactCount === 1
                    ? "1 affected connector uses an expression, function, or runtime-only value and may need review after deletion."
                    : `${advancedImpactCount} affected connectors use expressions, functions, or runtime-only values and may need review after deletion.`}
                </p>
              )}
            </div>
          ),
          okText: targetCount > 1 ? "Delete layers" : "Delete layer",
          okType: "text",
          okButtonProps: {
            id: "modal-ok",
            className: "layer-structural-confirm-modal__delete-button",
          },
          cancelText: "Cancel",
          cancelButtonProps: { className: "blings-btn", id: "modal-cancel" },
          onOk: async () => {
            setIsApplyingLayerAction(true);
            try {
              await playerStore.deleteLayerSelections(selections);
            } finally {
              setIsApplyingLayerAction(false);
            }
          },
        });
      } finally {
        setIsApplyingLayerAction(false);
      }
    },
    [isApplyingLayerAction, playerStore, showBlockedWarning]
  );

  const sortLayers = useCallback(
    async (
      action: LayerSortAction,
      { selections }: LayerStructuralActionArgs = {}
    ) => {
      if (isApplyingLayerAction) return;

      setIsApplyingLayerAction(true);
      try {
        const result = await playerStore.sortLayerSelections(
          action,
          selections
        );
        if (result?.blockedReason) {
          showBlockedWarning(result.blockedReason);
        }
      } finally {
        setIsApplyingLayerAction(false);
      }
    },
    [isApplyingLayerAction, playerStore, showBlockedWarning]
  );

  return {
    copyLayers,
    deleteLayers,
    duplicateLayers,
    pasteLayers,
    sortLayers,
    isApplyingLayerAction,
  };
}
